/**
 * Canonical query-key factory. NEVER use string-literal query keys in
 * feature code — always import from here. Adding a resource means
 * extending this file.
 */

export const qk = {
  brands: {
    all: ["brands"] as const,
    list: (access: string, query: object) => ["brands", "list", access, query] as const,
    sample: (access: string, query: object) => ["brands", "sample", access, query] as const,
    evidence: (access: string, query: object) => ["brands", "evidence", access, query] as const,
    trend: (access: string, query: object) => ["brands", "trend", access, query] as const,
    report: (access: string, query: object) => ["brands", "report", access, query] as const,
  },
  scanReports: {
    planograms: (sessionId: string) => ["scan-reports", sessionId, "planograms"] as const,
    all: ["scan-reports"] as const,
    issues: (sessionId: string) => ["scan-reports", sessionId, "issues"] as const,
  },
  auth: {
    me: ["auth", "me"] as const,
    sessions: ["auth", "sessions"] as const,
  },
  apiKeys: {
    all: ["api-keys"] as const,
    list: ["api-keys", "list"] as const,
  },
  users: {
    me: ["users", "me"] as const,
    myCompanies: ["users", "me", "companies"] as const,
    tenantUsers: (companyId: string) => ["users", "tenantUsers", companyId] as const,
    stores: (userId: string) => ["users", userId, "stores"] as const,
    unbound: ["users", "store-scoped-unbound"] as const,
  },
  invitations: {
    list: () => ["invitations", "list"] as const,
  },
  companies: {
    list: ["companies", "list"] as const,
    detail: (id: string) => ["companies", "detail", id] as const,
  },
  stores: {
    all: ["stores"] as const,
    list: (q: Record<string, unknown>) => ["stores", "list", q] as const,
    detail: (id: string) => ["stores", "detail", id] as const,
    categories: (storeId: string) => ["stores", "categories", storeId] as const,
    activeCategories: (storeId: string) =>
      ["stores", "categories", storeId, "active"] as const,
  },
  storeMaps: {
    list: (storeId: string) => ["store-maps", "list", storeId] as const,
    active: (storeId: string) => ["store-maps", "active", storeId] as const,
  },
  fixturePlanograms: {
    all: ["fixture-planograms"] as const,
    workspace: (access: string, id: string) =>
      ["fixture-planograms", access, "workspace", id] as const,
    versions: (access: string, id: string, page: number) =>
      ["fixture-planograms", access, "versions", id, page] as const,
    visionResponse: (access: string, id: string, source: string, revision: number) =>
      ["fixture-planograms", access, "vision-response", id, source, revision] as const,
    capture: (access: string, id: string) => ["fixture-planograms", access, "capture", id] as const,
    comparisons: (access: string, id: string, page: number) =>
      ["fixture-planograms", access, "comparisons", id, page] as const,
  },
  fixtures: {
    all: ["fixtures"] as const,
    scenePages: (access: string, storeId: string, mapId: string) =>
      ["fixtures", "scene-pages", access, storeId, mapId] as const,
    scene: (access: string, storeId: string, mapId: string) =>
      ["fixtures", "scene", access, storeId, mapId] as const,
    list: (q: Record<string, unknown>) => ["fixtures", "list", q] as const,
    listAll: (q: Record<string, unknown>) => ["fixtures", "list-all", q] as const,
    detail: (id: string) => ["fixtures", "detail", id] as const,
  },
  shelves: {
    scenes: ["shelves", "scene"] as const,
    scene: (access: string, storeId: string, mapId: string, fixtureIds: string[]) =>
      ["shelves", "scene", access, storeId, mapId, fixtureIds] as const,
    list: (fixtureId: string) => ["shelves", "list", fixtureId] as const,
  },
  categories: {
    all: ["categories"] as const,
    list: (q: Record<string, unknown>) => ["categories", "list", q] as const,
    detail: (id: string) => ["categories", "detail", id] as const,
  },
  products: {
    all: ["products"] as const,
    list: (q: Record<string, unknown>) => ["products", "list", q] as const,
    detail: (id: string) => ["products", "detail", id] as const,
  },
  productImages: {
    list: (productId: string) => ["product-images", "list", productId] as const,
  },
  productImports: {
    all: ["product-imports"] as const,
    list: (q: Record<string, unknown>) => ["product-imports", "list", q] as const,
    detail: (id: string) => ["product-imports", "detail", id] as const,
  },
  productExports: {
    all: ["product-exports"] as const,
    list: (q: Record<string, unknown>) => ["product-exports", "list", q] as const,
    detail: (id: string) => ["product-exports", "detail", id] as const,
  },
  productBarcodes: {
    list: (productId: string) => ["product-barcodes", "list", productId] as const,
  },
  productEmbeddings: {
    list: (productId: string) => ["product-embeddings", "list", productId] as const,
    runs: (productId: string, q: Record<string, unknown>) =>
      ["product-embeddings", "runs", productId, q] as const,
  },
  variantCandidates: {
    all: ["variant-candidates"] as const,
    list: (q: Record<string, unknown>) => ["variant-candidates", "list", q] as const,
  },
  captureDevices: {
    all: ["captureDevices"] as const,
    list: (query?: object) => ["captureDevices", "list", query] as const,
    detail: (id: string) => ["captureDevices", "detail", id] as const,
  },
  zones: {
    all: ["zones"] as const,
    list: (query?: object) => ["zones", "list", query] as const,
    listAll: (query?: object) => ["zones", "list-all", query] as const,
    detail: (id: string) => ["zones", "detail", id] as const,
  },
  planograms: {
    all: ["planograms"] as const,
    list: (query?: object) => ["planograms", "list", query] as const,
    detail: (id: string) => ["planograms", "detail", id] as const,
    positions: (planogramId: string) =>
      ["planograms", "positions", planogramId] as const,
    assignments: (query?: object) =>
      ["planograms", "assignments", query] as const,
    assignmentDetail: (id: string) =>
      ["planograms", "assignments", "detail", id] as const,
  },
  captureSessions: {
    all: ["capture-sessions"] as const,
    list: (q: Record<string, unknown>) => ["capture-sessions", "list", q] as const,
    detail: (id: string) => ["capture-sessions", "detail", id] as const,
    tasks: (id: string) => ["capture-sessions", "tasks", id] as const,
  },
  captureTasks: {
    all: ["capture-tasks"] as const,
    list: (q: Record<string, unknown>) => ["capture-tasks", "list", q] as const,
    detail: (id: string) => ["capture-tasks", "detail", id] as const,
  },
  captures: {
    all: ["captures"] as const,
    list: (taskId: string, q: Record<string, unknown>) =>
      ["captures", "list", taskId, q] as const,
    analysis: (captureId: string) => ["captures", "analysis", captureId] as const,
    shelfAnalytics: (captureId: string) =>
      ["captures", "shelf-analytics", captureId] as const,
    listForSession: (sessionId: string, q: Record<string, unknown>) =>
      ["captures", "session", sessionId, q] as const,
  },
  vision: {
    runs: (q: Record<string, unknown>) => ["vision", "runs", q] as const,
    run: (id: string) => ["vision", "run", id] as const,
  },
  issues: {
    all: ["issues"] as const,
    list: (q: Record<string, unknown>) => ["issues", "list", q] as const,
    detail: (id: string) => ["issues", "detail", id] as const,
  },
  storeTasks: {
    all: ["store-tasks"] as const,
    list: (q: Record<string, unknown>) => ["store-tasks", "list", q] as const,
    detail: (id: string) => ["store-tasks", "detail", id] as const,
  },
  webhooks: {
    all: ["webhooks"] as const,
    list: (q: Record<string, unknown>) => ["webhooks", "list", q] as const,
    detail: (id: string) => ["webhooks", "detail", id] as const,
    deliveries: (id: string, q: Record<string, unknown>) =>
      ["webhooks", "deliveries", id, q] as const,
    deliveryDetail: (deliveryId: string) =>
      ["webhooks", "delivery-detail", deliveryId] as const,
    eventTypes: ["webhooks", "event-types"] as const,
  },
  analytics: {
    all: ["analytics"] as const,
    locationMap: (access: unknown, params: Record<string, unknown>) => ["analytics", "location-map", access, params] as const,
    fixtureScans: (access: unknown, params: Record<string, unknown>) => ["analytics", "fixture-scans", access, params] as const,
    locations: (access: unknown, params: Record<string, unknown>) =>
      ["analytics", "locations", access, params] as const,
    summary: ["analytics", "summary"] as const,
    issues: ["analytics", "issues"] as const,
    tasks: ["analytics", "tasks"] as const,
    sessions: ["analytics", "sessions"] as const,
    products: ["analytics", "products"] as const,
    taskReport: (taskId: string) => ["analytics", "task-report", taskId] as const,
    sessionReport: (sessionId: string) =>
      ["analytics", "session-report", sessionId] as const,
    trends: (params: Record<string, unknown>) =>
      ["analytics", "trends", params] as const,
  },
  pricing: {
    capture: (id: string) => ["pricing", "capture", id] as const,
    session: (id: string) => ["pricing", "session", id] as const,
    task: (id: string) => ["pricing", "task", id] as const,
  },
  audit: {
    all: ["audit"] as const,
    list: (q: Record<string, unknown>) => ["audit", "list", q] as const,
    detail: (id: string) => ["audit", "detail", id] as const,
    facets: ["audit", "facets"] as const,
  },
} as const;
